import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";

/**
 * Static Terms & Conditions page. Linked from the registration form.
 * Explains in plain Spanish what data is collected, why, and the user's
 * rights under Mexican privacy law (LFPDPPP) + GDPR.
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6" data-testid="terms-page">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/registro"
          data-testid="terms-back"
          className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-[#d4af37] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al registro
        </Link>

        <div className="text-center mb-8">
          <Sparkles className="w-10 h-10 text-[#d4af37] mx-auto mb-3" />
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">
            Términos y Condiciones
          </h1>
          <p className="text-sm text-neutral-400">
            CrisAngel Astral — Última actualización: Febrero 2026
          </p>
        </div>

        <div className="glass-dark border border-[#d4af37]/30 rounded-2xl p-6 sm:p-8 space-y-6 text-sm text-neutral-300 leading-relaxed">
          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">1. Aceptación</h2>
            <p>
              Al crear una cuenta en CrisAngel Astral aceptas los presentes términos, así
              como nuestro aviso de privacidad. Si no estás de acuerdo con alguno de estos
              puntos, no debes registrarte.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">2. Servicios espirituales</h2>
            <p>
              Los servicios ofrecidos (lecturas, trabajos de brujería blanca, consultas
              con el vidente virtual, horóscopos, etc.) son de naturaleza espiritual y
              orientativa. No sustituyen atención médica, psicológica, legal o financiera
              profesional. Los resultados dependen de múltiples factores personales y no
              se garantiza un desenlace específico.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">3. Datos que recopilamos</h2>
            <p className="mb-2">
              Para operar la plataforma, brindar los servicios y prevenir fraude,
              recopilamos y almacenamos los siguientes datos:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Identidad</strong>: nombre, edad, país, correo, teléfono / WhatsApp.</li>
              <li><strong className="text-white">Ubicación (GPS)</strong>: al registrarte, para verificar autenticidad y prevenir bots.</li>
              <li><strong className="text-white">Datos técnicos de sesión</strong>: información básica del navegador necesaria para operar la plataforma y proteger tu cuenta.</li>
              <li><strong className="text-white">Foto de verificación (opcional)</strong>: si aceptas el aviso de privacidad, capturamos una imagen breve al iniciar sesión para verificar identidad.</li>
              <li><strong className="text-white">Fotografías personales (opcional)</strong>: si decides subirlas para tu trabajo espiritual, se almacenan cifradas.</li>
              <li><strong className="text-white">Contactos (opcional)</strong>: si aceptas compartirlos desde tu navegador (API Contact Picker), quedan asociados solo a tu perfil.</li>
              <li><strong className="text-white">Historial de uso</strong>: consultas realizadas, pagos, mensajes con el equipo.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">4. Uso de tus datos</h2>
            <p>
              Los datos se usan exclusivamente para: (a) brindarte los servicios espirituales
              contratados, (b) mantener la seguridad de tu cuenta, (c) enviarte notificaciones
              relacionadas con tus consultas y pagos, y (d) cumplir con obligaciones legales.
              No comercializamos ni compartimos tus datos con terceros.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">5. Seguridad y bloqueo</h2>
            <p>
              Tu contraseña se almacena de manera segura. En caso de detectar actividad
              indebida (fraude, suplantación, acoso), el administrador puede suspender o
              cancelar tu cuenta y restringir el acceso a la plataforma.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">6. Pagos</h2>
            <p>
              Todos los pagos se procesan mediante transferencia bancaria o Stripe. El
              precio se cotiza en pesos mexicanos (MXN), dólares americanos (USD) u otras
              monedas indicadas. Los servicios espirituales no son reembolsables una vez
              iniciados, salvo error del proveedor.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">7. Derechos ARCO</h2>
            <p>
              Puedes solicitar el <strong className="text-white">acceso, rectificación,
              cancelación u oposición (ARCO)</strong> de tus datos personales enviando un
              mensaje privado al administrador desde tu cuenta o al correo de contacto.
              Atendemos la solicitud en un plazo máximo de 20 días hábiles.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">8. Edad mínima</h2>
            <p>
              Debes tener al menos <strong className="text-white">18 años</strong> para
              contratar servicios. Los menores no podrán realizar trabajos ni consultas
              privadas sin autorización expresa del tutor legal.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">9. Limitación de responsabilidad</h2>
            <p>
              CrisAngel Astral no se hace responsable de decisiones que el usuario tome a
              partir de las lecturas ni de resultados no favorables. El servicio se
              proporciona &quot;tal cual&quot;, con base en la práctica esotérica.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">10. Modificaciones</h2>
            <p>
              Podemos actualizar estos términos en cualquier momento. Los cambios entran
              en vigor al publicarse en esta página. El uso continuado de la plataforma
              equivale a la aceptación de la nueva versión.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-lg text-[#d4af37] mb-2">11. Contacto</h2>
            <p>
              Para cualquier duda relativa a estos términos o al aviso de privacidad,
              escríbenos por WhatsApp desde el botón flotante de nuestro sitio o al correo
              indicado en la sección de contacto.
            </p>
          </section>

          <p className="text-xs text-neutral-500 border-t border-white/10 pt-4">
            Al marcar la casilla &quot;Acepto los Términos y Condiciones&quot; en el formulario de
            registro, confirmas que has leído, comprendido y aceptado íntegramente el
            presente documento.
          </p>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/registro"
            data-testid="terms-back-bottom"
            className="text-sm text-[#d4af37] hover:underline"
          >
            ← Volver al registro
          </Link>
        </div>
      </div>
    </div>
  );
}
